// texe-web/src/pages/FollowingFeed.tsx
import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost, apiDelete, refreshAccess } from "../lib/api";
import { type PostItem } from "../components/PostCard";
import { realtimeBus } from "../realtime/RealtimeBus";
import { useAuth } from "../context/AuthProvider";
import { useGlobalPostInteractions } from "../context/PostInteractionsProvider";
import PageWithAds from "../components/PageWithAds";
import InFeedAd from "../components/InFeedAd";
import RichText from "../components/RichText";
import "./Feed.css";

type FeedResponse = { ok: true; items: PostItem[]; nextAfter: string | null };

interface FollowState {
  [userId: string]: boolean;
}

function FollowingFeed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  // Use existing hooks
  const { toggleLike: toggleLikeHook, batchLoadInteractions, getInteraction } = useGlobalPostInteractions();
  
  // Posts
  const [items, setItems] = useState<PostItem[]>([]);
  const [nextAfter, setNextAfter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  // Follow state (simplified - not using batch API)
  const [followState, setFollowState] = useState<FollowState>({});

  // Display optimization (load 50, show 20 initially)
  const [displayCount, setDisplayCount] = useState(20);

  // Realtime
  const [newCount, setNewCount] = useState(0);

  // Admin/moderation
  const [menuVisible, setMenuVisible] = useState<string | null>(null);
  const [editModalPost, setEditModalPost] = useState<PostItem | null>(null);
  const [editText, setEditText] = useState("");

  // Avatar zoom
  const [avatarModalUrl, setAvatarModalUrl] = useState<string | null>(null);

  // Failed avatars tracking
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(new Set());

  // Skeleton loading
  const showSkeleton = loading && items.length === 0;

  function mergeUnique(existing: PostItem[], incoming: PostItem[]) {
    const seen = new Set(existing.map((x) => x.id));
    const dedup = incoming.filter((x) => !seen.has(x.id));
    return [...existing, ...dedup];
  }

  // Batch load metadata for posts
  const loadBatchMeta = useCallback(async (posts: PostItem[]) => {
    if (posts.length === 0) return;

    try {
      const postIds = posts.map((p) => p.id);

      // Always load interactions - hook handles user state internally
      await batchLoadInteractions(postIds);

      // Load follow states only if user is logged in
      if (user) {
        const authorIds = Array.from(new Set(posts.map((p) => p.author?.id).filter(Boolean))) as string[];
        if (authorIds.length > 0) {
          const res = await apiPost<{ ok: true; items: { id: string; following: boolean; followsMe: boolean }[] }>(
            "/api/follow/status/batch",
            { userIds: authorIds }
          );

          const newState: FollowState = {};
          res.items.forEach(({ id, following }) => {
            newState[id] = following;
          });
          setFollowState(newState);
        }
      }
    } catch (err) {
      console.error("Batch meta load error:", err);
    }
  }, [user, batchLoadInteractions]);

  const load = useCallback(async (initial = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      setError(null);
      const qs = initial ? "" : nextAfter ? `?after=${encodeURIComponent(nextAfter)}` : "";
      const res = await apiGet<FeedResponse>(`/api/posts/following${qs}`);
      
      if (initial) {
        setItems(res.items);
        setDisplayCount(Math.min(20, res.items.length));
      } else {
        setItems((cur) => mergeUnique(cur, res.items));
      }
      
      setNextAfter(res.nextAfter);
      
      // Batch load metadata - always load if we have posts, regardless of user
      if (res.items.length > 0) {
        await loadBatchMeta(res.items);
      }
    } catch (e: any) {
      setError(e?.message || "Takip akışı yüklenemedi");
    } finally {
      loadingRef.current = false;
      if (initial) setLoading(false);
      if (!initial) setMoreLoading(false);
    }
  }, [nextAfter, loadBatchMeta]);

  // Load follow states for all authors in current feed
  const loadFollowStates = useCallback(async () => {
    if (!user) return;

    try {
      const authorIds = Array.from(new Set(items.map((p) => p.author?.id).filter(Boolean))) as string[];
      if (authorIds.length === 0) return;

      // Batch check follow status
      const res = await apiPost<{ ok: true; items: { id: string; following: boolean; followsMe: boolean }[] }>(
        "/api/follow/status/batch",
        { userIds: authorIds }
      );

      const newState: FollowState = {};
      res.items.forEach(({ id, following }) => {
        newState[id] = following;
      });
      setFollowState(newState);
    } catch (err) {
      console.error("Follow state load error:", err);
    }
  }, [user, items]);

  // İlk yükleme: token hazırla + veriyi çek + follow state yükle
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refreshAccess().catch(() => {});
      if (!cancelled) {
        await load(true);
        // Load follow states for displayed users
        if (user) {
          await loadFollowStates();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Realtime sinyaller
  useEffect(() => {
    const offNew = realtimeBus.onFeedNewPost(() => setNewCount((c) => c + 1));
    const offDel = realtimeBus.onFeedDeletePost(({ postId }) =>
      setItems((curr) => curr.filter((x) => x.id !== postId))
    );
    return () => {
      offNew();
      offDel();
    };
  }, []);

  // Reload follow states when items change
  useEffect(() => {
    if (user && items.length > 0) {
      loadFollowStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, user?.id]);

  async function loadMore() {
    if (!nextAfter || moreLoading) return;
    setMoreLoading(true);
    await load(false);
  }

  async function showNew() {
    setNewCount(0);
    await load(true);
  }

  // Follow/Unfollow
  const toggleFollow = useCallback(
    async (authorId: string) => {
      if (!user || !authorId) return;

      const currentlyFollowing = followState[authorId];

      // Optimistic update
      setFollowState((prev) => ({ ...prev, [authorId]: !currentlyFollowing }));

      try {
        if (currentlyFollowing) {
          await apiDelete(`/api/follow/${authorId}`);
        } else {
          await apiPost(`/api/follow/${authorId}`, {});
        }
      } catch (err: any) {
        console.error("Follow toggle error:", err);
        
        // API throws Error with message from backend's error field
        const errorMsg = err?.message || '';
        
        if (errorMsg === 'already_following') {
          // User is already following, keep the state as following
          setFollowState((prev) => ({ ...prev, [authorId]: true }));
          return;
        }
        
        if (errorMsg === 'not_following') {
          // User is not following, keep the state as not following
          setFollowState((prev) => ({ ...prev, [authorId]: false }));
          return;
        }
        
        // Revert on other errors
        setFollowState((prev) => ({ ...prev, [authorId]: currentlyFollowing }));
      }
    },
    [user, followState]
  );

  // Like/Unlike - use the hook
  const toggleLike = useCallback(
    async (postId: string) => {
      if (!user) return;
      try {
        await toggleLikeHook(postId);
      } catch (err) {
        console.error("Like toggle error:", err);
      }
    },
    [user, toggleLikeHook]
  );

  // Admin: Delete post
  const handleDelete = useCallback(
    async (postId: string) => {
      if (!user?.isAdmin) return;
      if (!window.confirm("Bu gönderiyi silmek istediğinizden emin misiniz?")) return;

      try {
        await apiDelete(`/api/posts/${postId}`);
        setItems((curr) => curr.filter((x) => x.id !== postId));
        setMenuVisible(null);
      } catch (err) {
        console.error("Delete error:", err);
        alert("Gönderi silinemedi");
      }
    },
    [user]
  );

  // Admin: Edit post
  const handleEditOpen = useCallback((post: PostItem) => {
    setEditModalPost(post);
    setEditText(post.text || "");
    setMenuVisible(null);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editModalPost || !user?.isAdmin) return;

    try {
      await apiPost(`/api/admin/posts/${editModalPost.id}`, { text: editText });
      setItems((curr) =>
        curr.map((x) => (x.id === editModalPost.id ? { ...x, text: editText } : x))
      );
      setEditModalPost(null);
      setEditText("");
    } catch (err) {
      console.error("Edit error:", err);
      alert("Gönderi düzenlenemedi");
    }
  }, [editModalPost, editText, user]);

  // Admin: Bulk like
  const handleBulkLike = useCallback(
    async (postId: string) => {
      if (!user?.isAdmin) return;

      const count = window.prompt("Kaç beğeni eklensin?", "10");
      if (!count) return;

      const num = parseInt(count, 10);
      if (isNaN(num) || num <= 0) {
        alert("Geçerli bir sayı girin");
        return;
      }

      try {
        await apiPost(`/api/admin/bulk-like/${postId}`, { count: num });
        // Reload interactions to get updated count
        await batchLoadInteractions([postId]);
        setMenuVisible(null);
        // Reload feed to ensure consistency
        await load(true);
      } catch (err) {
        console.error("Bulk like error:", err);
        alert("Toplu beğeni eklenemedi");
      }
    },
    [user, batchLoadInteractions, load]
  );

  // Admin: Add 1000 likes
  const handle1000LikeAdd = useCallback(
    async (postId: string) => {
      if (!user?.isAdmin) return;

      try {
        await apiPost(`/api/admin/bulk-like/${postId}`, { count: 1000 });
        // Force complete reload of interactions to get accurate counts
        await batchLoadInteractions([postId]);
        setMenuVisible(null);
        // Optionally reload the entire feed to ensure consistency
        await load(true);
        alert("1000 like eklendi");
      } catch (err) {
        console.error("1000 like add error:", err);
        alert("1000 like eklenirken hata oluştu");
      }
    },
    [user, batchLoadInteractions, load]
  );

  // Admin: Remove 1000 likes
  const handle1000LikeRemove = useCallback(
    async (postId: string) => {
      if (!user?.isAdmin) return;

      if (!window.confirm("Son eklenen 1000 like'ı silmek istiyor musunuz?")) {
        return;
      }

      try {
        await apiPost(`/api/admin/remove-bulk-like/${postId}`, {});
        // Force complete reload of interactions to get accurate counts
        await batchLoadInteractions([postId]);
        setMenuVisible(null);
        // Optionally reload the entire feed to ensure consistency
        await load(true);
        alert("1000 like geri alındı");
      } catch (err: any) {
        console.error("1000 like remove error:", err);
        const msg = err?.message || "1000 like geri alınırken hata oluştu";
        alert(msg);
      }
    },
    [user, batchLoadInteractions, load]
  );

  // Avatar helpers
  const getUserInitial = (username?: string) => {
    return username ? username.charAt(0).toUpperCase() : "?";
  };

  const handleAvatarError = (userId: string) => {
    setFailedAvatars((prev) => new Set(prev).add(userId));
  };

  const handleAvatarClick = (url?: string) => {
    if (url) {
      setAvatarModalUrl(url);
    }
  };

  // Display count optimization
  const visibleItems = items.slice(0, displayCount);
  const hasHiddenItems = displayCount < items.length;

  const showMoreDisplayed = () => {
    setDisplayCount((prev) => Math.min(prev + 20, items.length));
  };

  // Render helpers
  const renderSkeleton = () => (
    <div className="feed-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="skeleton-post">
          <div className="skeleton-header">
            <div className="skeleton-avatar" />
            <div className="skeleton-info">
              <div className="skeleton-username" />
              <div className="skeleton-timestamp" />
            </div>
          </div>
          <div className="skeleton-text" />
          <div className="skeleton-text" />
          <div className="skeleton-text" />
        </div>
      ))}
    </div>
  );

  const renderPostHeader = (post: PostItem) => {
    const isMyPost = post.author?.id === user?.id;
    const avatarFailed = post.author?.id ? failedAvatars.has(post.author.id) : false;
    const isGold = post.author?.isAdmin;

    return (
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, paddingRight: 50 }}>
          {/* Avatar */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              overflow: "hidden",
              cursor: "pointer",
              border: isGold ? "3px solid gold" : "2px solid #114232",
            }}
            onClick={() => handleAvatarClick(post.author?.avatarUrl || undefined)}
          >
            {avatarFailed || !post.author?.avatarUrlSmall ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(135deg, #114232, #1a5a42)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 20,
                  fontWeight: 700,
                }}
              >
                {getUserInitial(post.author?.username)}
              </div>
            ) : (
              <img
                src={post.author.avatarUrlSmall}
                alt={post.author.username}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => post.author?.id && handleAvatarError(post.author.id)}
              />
            )}
          </div>

          {/* User info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontWeight: 700,
                  color: isGold ? "gold" : "#114232",
                  cursor: "pointer",
                }}
                onClick={() => post.author?.id && navigate(`/profile/${post.author.id}`)}
              >
                {post.author?.username || "Anonim"}
                {post.author?.totalStars && post.author.totalStars > 0 && (
                  <span style={{ color: "#FFD700", fontSize: 13, marginLeft: 4 }}>
                    ⭐{post.author.totalStars.toFixed(1)}
                  </span>
                )}
              </span>
              {isGold && (
                <span
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    background: "rgba(255, 215, 0, 0.12)",
                    color: "gold",
                    borderRadius: 999,
                    border: "1px solid rgba(255, 215, 0, 0.7)",
                    fontWeight: 600,
                    letterSpacing: 0.2,
                  }}
                >
                  GOLD
                </span>
              )}
              {post.author?.isAdmin && (
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 6px",
                    background: "#FFD700",
                    color: "#000",
                    borderRadius: 4,
                    fontWeight: 700,
                  }}
                >
                  ADMIN
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>
              {new Date(post.createdAt).toLocaleString("tr-TR")}
            </div>
          </div>
        </div>

        {/* Menu button - absolute positioned in top right */}
        {(user?.isAdmin || isMyPost || (!isMyPost && post.author?.id)) && (
          <button
            onClick={() => setMenuVisible(menuVisible === post.id ? null : post.id)}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              padding: "8px",
              borderRadius: 6,
              border: "1px solid #666",
              background: "transparent",
              color: "#666",
              fontSize: 20,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ⋯
          </button>
        )}
      </div>
    );
  };

  const renderPostActions = (post: PostItem) => {
    const interaction = getInteraction(post.id);

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12, paddingTop: 12, borderTop: "1px solid #2c2c2c" }}>
        {/* Like */}
        <button
          onClick={() => toggleLike(post.id)}
          disabled={interaction.loading}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 6,
            border: interaction.liked ? "1px solid #114232" : "1px solid #666",
            background: interaction.liked ? "rgba(17, 66, 50, 0.2)" : "transparent",
            color: interaction.liked ? "#114232" : "#666",
            fontSize: 14,
            fontWeight: 600,
            cursor: interaction.loading ? "not-allowed" : "pointer",
            opacity: interaction.loading ? 0.6 : 1,
          }}
        >
          <span>{interaction.liked ? "❤️" : "🤍"}</span>
          <span>{interaction.likeCount}</span>
        </button>

        {/* Comments */}
        <button
          onClick={() => navigate(`/post/${post.id}`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #666",
            background: "transparent",
            color: "#666",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <span>💬</span>
          <span>{interaction.commentCount}</span>
        </button>

        {/* Share */}
        <button
          onClick={() => {
            const url = `${window.location.origin}/post/${post.id}`;
            navigator.clipboard.writeText(url).then(() => {
              alert("Link kopyalandı!");
            }).catch(() => {
              alert("Link kopyalanamadı");
            });
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #666",
            background: "transparent",
            color: "#666",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <span>🔗</span>
        </button>
      </div>
    );
  };

  const renderAdminMenu = (post: PostItem) => {
    if (menuVisible !== post.id) return null;

    const isMyPost = post.author?.id === user?.id;

    return (
      <div
        style={{
          position: "absolute",
          top: 50,
          right: 16,
          background: "#1a1a1a",
          border: "2px solid #114232",
          borderRadius: 8,
          padding: 8,
          zIndex: 1000,
          minWidth: 200,
        }}
      >
        {/* Admin Options */}
        {user?.isAdmin && (
          <>
            <button
              onClick={() => handleEditOpen(post)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ✏️ Düzenle (Admin)
            </button>
            <button
              onClick={() => handle1000LikeAdd(post.id)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ⚡ 1000 Like Ekle
            </button>
            <button
              onClick={() => handle1000LikeRemove(post.id)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              🔄 1000 Like Geri Al
            </button>
            <button
              onClick={() => handleBulkLike(post.id)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ⭐ Özel Like Sayısı
            </button>
            <button
              onClick={() => handleDelete(post.id)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#dc2626",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              🗑️ Sil (Admin)
            </button>
          </>
        )}

        {/* Own Post Options */}
        {isMyPost && !user?.isAdmin && (
          <>
            <button
              onClick={() => handleEditOpen(post)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ✏️ Düzenle
            </button>
            <button
              onClick={() => handleDelete(post.id)}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#dc2626",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              🗑️ Sil
            </button>
          </>
        )}

        {/* Other User's Post Options */}
        {!isMyPost && post.author?.id && (
          <>
            <button
              onClick={() => {
                setMenuVisible(null);
                if (post.author?.id && window.confirm(`${post.author.username} kullanıcısını takip etmeyi bırakmak istiyor musunuz?`)) {
                  toggleFollow(post.author.id);
                }
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              👋 Takibi Bırak
            </button>
            <button
              onClick={() => {
                setMenuVisible(null);
                if (window.confirm(`${post.author?.username} kullanıcısını şikayet etmek istiyor musunuz?`)) {
                  apiPost(`/api/reports/post/${post.id}`, {})
                    .then(() => alert("Şikayet gönderildi"))
                    .catch(() => alert("Şikayet gönderilemedi"));
                }
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ⚠️ Şikayet Et
            </button>
            <button
              onClick={() => {
                setMenuVisible(null);
                if (window.confirm(`${post.author?.username} kullanıcısını engellemek istiyor musunuz?`)) {
                  apiPost(`/api/blocks/${post.author?.id}`, {})
                    .then(() => {
                      alert("Kullanıcı engellendi");
                      setItems((curr) => curr.filter((x) => x.author?.id !== post.author?.id));
                    })
                    .catch(() => alert("Engelleme işlemi başarısız"));
                }
              }}
              style={{
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              🚫 Engelle
            </button>
          </>
        )}

        {/* Cancel Button */}
        <button
          onClick={() => setMenuVisible(null)}
          style={{
            width: "100%",
            padding: "8px 12px",
            textAlign: "left",
            border: "none",
            background: "transparent",
            color: "#666",
            fontSize: 14,
            cursor: "pointer",
            marginTop: 4,
            borderTop: "1px solid #2c2c2c",
          }}
        >
          İptal
        </button>
      </div>
    );
  };

  if (showSkeleton) {
    return (
      <div className="feed-container">
        <div className="feed-content">
          <div className="feed-header">
            <h1 className="feed-title">Takip Akışı</h1>
          </div>
          {renderSkeleton()}
        </div>
      </div>
    );
  }

  return (
    <div className="feed-container">
      <div className="feed-content">
        {/* Header */}
        <div className="feed-header">
          <h1 className="feed-title">Takip Akışı</h1>
          <div className="feed-actions">
            <button
              className="feed-button"
              onClick={() => load(true)}
              disabled={loadingRef.current}
            >
              Yenile
            </button>
            <Link to="/new" className="feed-button feed-button-primary">
              Paylaş
            </Link>
          </div>
        </div>

        {/* New posts badge */}
        {newCount > 0 && (
          <div className="feed-new-posts-badge" onClick={showNew}>
            <span className="feed-new-posts-badge-text">
              ✨ {newCount} yeni gönderi var
            </span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="feed-error">
            <p className="feed-error-text">{error}</p>
            <button className="feed-button" onClick={() => load(true)}>
              Tekrar dene
            </button>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && !error && (
          <div className="feed-empty">Takip ettiklerinden henüz paylaşım yok.</div>
        )}

        {/* Posts */}
        <div className="feed-posts">
          {visibleItems.map((post, index) => {
            // Check if post is from admin (gold styling)
            const isGold = post.author?.isAdmin;
            
            return (
              <div key={post.id}>
                {/* Show In-Feed Ad every 10 posts */}
                {index > 0 && index % 10 === 0 && (
                  <InFeedAd slot={`FOLLOWING_INFEED_${Math.floor(index / 10)}`} />
                )}
                
                <div
                  style={{
                    position: "relative",
                    background: isGold ? "rgba(34, 27, 0, 0.35)" : "#1a1a1a",
                    border: isGold ? "2px solid rgba(255, 215, 0, 0.7)" : "2px solid #114232",
                    borderRadius: 16,
                    padding: 16,
                    boxShadow: isGold ? "0 0 16px rgba(255, 215, 0, 0.3)" : "none",
                  }}
                >
                  {renderPostHeader(post)}
                  
                  {/* Post content - clickable to go to detail */}
                  <div
                    onClick={() => navigate(`/post/${post.id}`)}
                    style={{
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: isGold ? "gold" : "#fff",
                      marginBottom: 12,
                      cursor: "pointer",
                    }}
                  >
                    <RichText text={post.text} />
                  </div>

                  {renderPostActions(post)}
                  {renderAdminMenu(post)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Show more displayed items */}
        {hasHiddenItems && (
          <button
            className="feed-button feed-button-primary"
            onClick={showMoreDisplayed}
            style={{ width: "100%", marginTop: 16 }}
          >
            Daha Fazla Göster ({items.length - displayCount} gizli)
          </button>
        )}

        {/* Load more from server */}
        {!hasHiddenItems && (
          <button
            className="feed-load-more"
            disabled={!nextAfter || moreLoading}
            onClick={loadMore}
          >
            {nextAfter
              ? moreLoading
                ? "Yükleniyor..."
                : "Daha Fazla Yükle"
              : "Hepsi bu kadar"}
          </button>
        )}

        {!nextAfter && items.length > 0 && (
          <div className="feed-end">🎉 Tüm gönderileri gördünüz!</div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalPost && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
          onClick={() => setEditModalPost(null)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "2px solid #114232",
              borderRadius: 16,
              padding: 24,
              maxWidth: 600,
              width: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: "#114232" }}>Gönderiyi Düzenle</h2>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              style={{
                width: "100%",
                minHeight: 200,
                padding: 12,
                borderRadius: 8,
                border: "2px solid #114232",
                background: "#0a0a0a",
                color: "#fff",
                fontSize: 15,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
              <button
                className="feed-button feed-button-primary"
                onClick={handleEditSave}
              >
                Kaydet
              </button>
              <button
                className="feed-button"
                onClick={() => setEditModalPost(null)}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar Modal */}
      {avatarModalUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
          onClick={() => setAvatarModalUrl(null)}
        >
          <img
            src={avatarModalUrl}
            alt="Avatar"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: 16,
              border: "4px solid #114232",
            }}
          />
        </div>
      )}
    </div>
  );
}

// Wrap with ads
export default function FollowingFeedWithAds() {
  return (
    <PageWithAds leftAdSlot="4060917552" rightAdSlot="6230009221">
      <FollowingFeed />
    </PageWithAds>
  );
}
